import { FetchApi } from '../lib/anuket-http';
import { configs } from 'configs';
import { streamHook, eventBus } from './Streams';
import { getToken } from '../utils/isLoggedIn';
import {normalizeHHMMSS} from '../utils/date';
import {streamFail} from './Streams';

/**
 * Get current course
 *
 * @param {number} eventId
 * @return {ReplaySubject}
 */
export function getCourse(eventId) {
  return streamHook(`start-list-${eventId}`, '');
}

/**
 * Set current course
 *
 * @param {number} eventId
 * @param {number} courseId
 */
export function setCourse(eventId, courseId) {
  eventBus.publish(`start-list-${eventId}`, courseId);
}

/**
 * Loads event data and publish currently selected event
 *
 * @param {number} eventId
 */
export function loadEvent(eventId) {
  const url = `${configs.ATHLINKS_API_HOST}/events/race/api/${eventId}`;
  return FetchApi.doFetch(url)
    .map(({ Result }) => {
      return Result;
    })
    .do((result) => {
      setCourse(eventId, extractSelectedCourseId(result.SelectedCourseData));
      return result;
    });
}

/**
 * Extract course id from selected course string
 *
 * @param {string} course
 */
export const extractSelectedCourseId = (course) => course.split(':')[1];

/**
 * Loads starters for event id and selected course
 * @param {number} eventId
 */
export function getStarters({ eventId }) {
  const url = `${configs.ATHLINKS_API_HOST}/Events/Race/Api/${eventId}`;
  return getCourse(eventId)
    .filter((courseId) => courseId > 0)
    .flatMap((courseId) => FetchApi.doFetch(`${url}/StartList/${courseId}`))
    .map(({Result: {RacerList}}) => RacerList || {});
  /**
   * @TODO: Taking only first elemtn from an array is probably wrong....
   */
}

// START LIST USER ENTRIES
const userStartListEntryStreamId = 'user-startlist-entries';

export function fetchUserStartListEntries(eventId) {
  getToken().then((token) => {
    const streamId = `${userStartListEntryStreamId}-${eventId}`;
    const url = `${configs.ATHLINKS_API_HOST}/Events/Race/Api/StartListForCurrentUser/${eventId}`;

    streamFail(
      streamId,
      FetchApi.doFetch(
        url,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      ),
      (msg) => {
        return msg.ErrorMessage ? {error: msg.ErrorMessage} : {result: msg.Result};
      }
    );
  });
}

export function getUserStartListEntries(eventId) {
  return streamHook(`${userStartListEntryStreamId}-${eventId}`, {fetching: true});
}

// ROSTER USER ENTRIES
const userRosterEntryStreamId = 'user-roster-entries';

export function fetchUserRosterEntries(eventId) {
  getToken().then((token) => {
    const streamId = `${userRosterEntryStreamId}-${eventId}`;
    const url = `${configs.ATHLINKS_API_HOST}/Events/Race/Api/GetRosterResponseForCurrentUser/${eventId}`;

    streamFail(
      streamId,
      FetchApi.doFetch(
        url,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      ),
      (msg) => {
        return (msg || {}).ErrorMessage ? {error: msg.ErrorMessage} : {result: (msg || {}).Result};
      }
    );
  });
}

export function getUserRosterEntries(eventId) {
  return streamHook(`${userRosterEntryStreamId}-${eventId}`, {fetching: true});
}

/**
 * Adds event to calendar for authorized user
 *
 * @param {number} eventId
 * @param {number} CourseID
 * @param {number} EventCourseID
 * @param {string} RaceGoals
 * @param {string} TargetTime
 * @return {*}
 */
export async function saveEventToCalendar({EventID, EventCourseID, CourseID, RaceGoals = '', TargetTime = ''}) {
  const token = await getToken();
  const url = `${configs.ATHLINKS_API_HOST}/Events/Race/Api/${EventID}` + `/StartList/${EventCourseID}/Save`;

  // normalize target time
  const targetTime = !!TargetTime ? normalizeHHMMSS(TargetTime) : TargetTime;

  return FetchApi.doFetch(
    url,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        CourseID,
        RaceGoals,
        TargetTime: targetTime
      })
    }
  );
}

/**
 *
 * Remove event from calendar for authorized user
 *
 * @param eventId
 * @param eventCourseId
 * @returns {*}
 */
export async function removeEventFromCalendar({EventID, EventCourseID}) {
  const token = await getToken();
  const url = `${configs.ATHLINKS_API_HOST}/Events/Race/Api/${EventID}/StartList/${EventCourseID}/Remove`;
  return FetchApi.doFetch(
    url,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    }
  );
}

/**
 * Converts starters object to array with starters
 * Example: {race1: athleteArr1, race2: AthleteArr2} -> [...athleteArr1, ...athleteArr2]
 * @param {*} starters
 * @return {Array}
 */
export function normalizeStarters(starters) {
  if(!starters) return [];
  else return Object.keys(starters).reduce(
    (acc, race) => [...acc, ...starters[race]], []);
}

const eventCourseStartListStreamId = 'event-course-start-list';

export function fetchStartListForEventCourse(eventId, eventCourseId, page) {
  const url = `${configs.ATHLINKS_API_HOST}/Events/Race/Api/${eventId}/StartList/${eventCourseId}?page=${page + 1}&pageSize=${configs.numResultsPerPage}`;
  eventBus.publish(`${eventCourseStartListStreamId}-${eventId}`, {fetching: true});

  FetchApi
    .doFetch(url)
    .subscribe(({Result: {RacerList, MasterCount}}) => {
      setStartListForEventCourse(eventId, normalizeStarters(RacerList), MasterCount)
    });
}

function setStartListForEventCourse(eventId, starters, totalEntries) {
  eventBus.publish(`${eventCourseStartListStreamId}-${eventId}`, {starters, totalEntries});
}

export function getStartListForEventCourse(eventId) {
  return streamHook(`${eventCourseStartListStreamId}-${eventId}`, {fetching: true});
}

// ROSTER
const eventCourseRosterStreamId = 'event-course-roster';

export function fetchRosterForEventCourse(eventId, eventCourseId, page) {
  const baseUrl = `${configs.ATHLINKS_API_HOST}/Affiliate/Api/CTLive/GetCtLiveRosterByEventCourseId/${eventCourseId}`;
  const params = `?startingIndex=${page * configs.numResultsPerPage}&pageSize=${configs.numResultsPerPage}`;
  const url = `${baseUrl}${params}`;

  eventBus.publish(`${eventCourseRosterStreamId}-${eventId}`, {fetching: true});

  FetchApi
    .doFetch(url)
    .subscribe(({StartListEntryRosterViewModels, MasterCount}) => {
      setRosterForEventCourse(eventId, StartListEntryRosterViewModels, MasterCount)
    });
}

function setRosterForEventCourse(eventId, starters, totalEntries) {
  eventBus.publish(`${eventCourseRosterStreamId}-${eventId}`, {starters, totalEntries});
}

export function getRosterForEventCourse(eventId) {
  return streamHook(`${eventCourseRosterStreamId}-${eventId}`, {fetching: true});
}

export function setEventCourse(masterId, eventCourseParams) {
  eventBus.publish(`event-course-${masterId}`, eventCourseParams);
}

export function getEventCourse(masterId) {
  return streamHook(`event-course-${masterId}`);
}

// PAGING
const startListPageStreamId = 'sl-page';

export function getPage() {
  return streamHook(startListPageStreamId).map((s) => parseInt(s.page));
}

export function setPage(page) {
  eventBus.publish(startListPageStreamId, {page});
}



/** WEBPACK FOOTER **
 ** ./data/StartListStreams.js
 **/